import axios from "axios";

export interface ValidAction {
	code: string;
	label: string;
}

export interface ActionDefinition {
	id: string;
	action_code: string;
	reputation_awarded: number;
	badges_awarded: string[];
}

export interface BadgeDefinitionManage {
	id: string;
	name: string;
	icon: string;
	description: string;
}

export interface LevelRequirement {
	id?: string;
	action_code?: string;
	action_definition_id: string;
	amount: number;
}

export interface GoalLevel {
	id: string;
	level_value: number;
	reputation_awarded: number;
	requirements: LevelRequirement[];
	badges_awarded: string[];
}

export interface GoalManage {
	id: string;
	name: string;
	levels: GoalLevel[];
}

export async function getValidActions(): Promise<ValidAction[]> {
	const r = await axios.get("/gamification/valid-actions/");
	return r.data;
}

export async function getOrCreateGamificationContext(courseId: string) {
	try {
		const r = await axios.get(`/courses/${courseId}/gamification_context/`);
		return r.data;
	} catch {
		const r = await axios.post(`/courses/${courseId}/gamification_context/`, {});
		return r.data;
	}
}

// Actions
export async function listActions(ctxId: string): Promise<ActionDefinition[]> {
	const r = await axios.get(`/gamification/contexts/${ctxId}/actions/`);
	return r.data;
}
export async function createAction(
	ctxId: string,
	data: Partial<ActionDefinition>,
): Promise<ActionDefinition> {
	const r = await axios.post(`/gamification/contexts/${ctxId}/actions/`, data);
	return r.data;
}
export async function updateAction(
	ctxId: string,
	id: string,
	data: Partial<ActionDefinition>,
): Promise<ActionDefinition> {
	const r = await axios.patch(`/gamification/contexts/${ctxId}/actions/${id}/`, data);
	return r.data;
}
export async function deleteAction(ctxId: string, id: string): Promise<void> {
	await axios.delete(`/gamification/contexts/${ctxId}/actions/${id}/`);
}

// Badges
export async function listBadges(ctxId: string): Promise<BadgeDefinitionManage[]> {
	const r = await axios.get(`/gamification/contexts/${ctxId}/badges/`);
	return r.data;
}
export async function createBadge(
	ctxId: string,
	data: Partial<BadgeDefinitionManage>,
): Promise<BadgeDefinitionManage> {
	const r = await axios.post(`/gamification/contexts/${ctxId}/badges/`, data);
	return r.data;
}
export async function updateBadge(
	ctxId: string,
	id: string,
	data: Partial<BadgeDefinitionManage>,
): Promise<BadgeDefinitionManage> {
	const r = await axios.patch(`/gamification/contexts/${ctxId}/badges/${id}/`, data);
	return r.data;
}
export async function deleteBadge(ctxId: string, id: string): Promise<void> {
	await axios.delete(`/gamification/contexts/${ctxId}/badges/${id}/`);
}

// Goals
export async function listGoals(ctxId: string): Promise<GoalManage[]> {
	const r = await axios.get(`/gamification/contexts/${ctxId}/goals/`);
	return r.data;
}
export async function createGoal(
	ctxId: string,
	data: { name: string },
): Promise<GoalManage> {
	const r = await axios.post(`/gamification/contexts/${ctxId}/goals/`, data);
	return r.data;
}
export async function updateGoal(
	ctxId: string,
	id: string,
	data: { name: string },
): Promise<GoalManage> {
	const r = await axios.patch(`/gamification/contexts/${ctxId}/goals/${id}/`, data);
	return r.data;
}
export async function deleteGoal(ctxId: string, id: string): Promise<void> {
	await axios.delete(`/gamification/contexts/${ctxId}/goals/${id}/`);
}

// Goal Levels
export async function listGoalLevels(
	ctxId: string,
	goalId: string,
): Promise<GoalLevel[]> {
	const r = await axios.get(`/gamification/contexts/${ctxId}/goals/${goalId}/levels/`);
	return r.data;
}
export async function createGoalLevel(
	ctxId: string,
	goalId: string,
	data: Partial<GoalLevel>,
): Promise<GoalLevel> {
	const r = await axios.post(
		`/gamification/contexts/${ctxId}/goals/${goalId}/levels/`,
		data,
	);
	return r.data;
}
export async function updateGoalLevel(
	ctxId: string,
	goalId: string,
	id: string,
	data: Partial<GoalLevel>,
): Promise<GoalLevel> {
	const r = await axios.patch(
		`/gamification/contexts/${ctxId}/goals/${goalId}/levels/${id}/`,
		data,
	);
	return r.data;
}
export async function deleteGoalLevel(
	ctxId: string,
	goalId: string,
	id: string,
): Promise<void> {
	await axios.delete(`/gamification/contexts/${ctxId}/goals/${goalId}/levels/${id}/`);
}
